import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FIELD_TABS, getCurrentMonthRange } from './constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Auth header helper
const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

const MultiReportsContext = React.createContext(null);

export function MultiReportsProvider({ children }) {
  const [bookings, setBookings] = React.useState([]);
  const [filteredBookings, setFilteredBookings] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [searchValue, setSearchValue] = React.useState('');
  const defaultRange = getCurrentMonthRange();
  const [dateRange, setDateRange] = React.useState({ from: defaultRange.from, to: defaultRange.to });
  const [selectedBookings, setSelectedBookings] = React.useState([]);
  const [activeFieldTab, setActiveFieldTab] = React.useState('rto_registration');
  
  // Tab-specific filters
  const [discountFilters, setDiscountFilters] = React.useState({
    pool_amount: 'all',
    scheme_discount_1: 'all',
    scheme_discount_2: 'all',
    special_discount: 'all'
  });
  
  const [otherFieldsFilters, setOtherFieldsFilters] = React.useState({
    inwards_status: 'all',
    address_proof_received: 'all',
    photos: 'all',
    loan_status: 'all'
  });
  
  const [pmeFilters, setPmeFilters] = React.useState({
    pme_submission_date: 'all',
    pme_status: 'all'
  });
  
  // Bulk edit state
  const [showBulkEditModal, setShowBulkEditModal] = React.useState(false);
  const [bulkEditData, setBulkEditData] = React.useState({});
  
  // Inline editing state
  const [editingCell, setEditingCell] = React.useState(null);
  const [editValue, setEditValue] = React.useState('');

  // Active tab info
  const activeTab = FIELD_TABS.find(t => t.id === activeFieldTab);
  const activeFields = activeTab?.fields || [];
  const dateFieldForTab = activeTab?.dateField || 'date_of_booking';

  // Fetch bookings
  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bookings?limit=10000`, getAuthHeader());
      const sorted = response.data.sort((a, b) => {
        const dateA = a.dms_invoice_date ? new Date(a.dms_invoice_date) : new Date(0);
        const dateB = b.dms_invoice_date ? new Date(b.dms_invoice_date) : new Date(0);
        if (dateB.getTime() !== dateA.getTime()) return dateB - dateA;
        return (parseInt(b.booking_no) || 0) - (parseInt(a.booking_no) || 0);
      });
      setBookings(sorted);
    } catch (error) {
      toast.error('Failed to fetch bookings');
    }
    setLoading(false);
  };

  // Filter bookings based on all filters
  const filterBookings = React.useCallback(() => {
    let filtered = [...bookings];

    // Date filter based on active tab's date field
    if (dateRange.from) {
      filtered = filtered.filter(b => {
        const dateValue = b[dateFieldForTab];
        if (!dateValue) return false;
        return new Date(dateValue) >= new Date(dateRange.from);
      });
    }
    if (dateRange.to) {
      const toDate = new Date(dateRange.to);
      toDate.setHours(23, 59, 59);
      filtered = filtered.filter(b => {
        const dateValue = b[dateFieldForTab];
        if (!dateValue) return false;
        return new Date(dateValue) <= toDate;
      });
    }
    
    // Search filter
    if (searchValue.trim()) {
      const search = searchValue.toLowerCase();
      filtered = filtered.filter(b =>
        b.booking_no?.toString().includes(search) ||
        b.customer_first_name?.toLowerCase().includes(search) ||
        b.customer_last_name?.toLowerCase().includes(search) ||
        b.mobile_no_1?.includes(search) ||
        b.registration_number?.toLowerCase().includes(search) ||
        b.chassis_no?.toLowerCase().includes(search)
      );
    }

    // Apply discount filters when on discounts tab
    if (activeFieldTab === 'discounts') {
      Object.entries(discountFilters).forEach(([field, filterValue]) => {
        if (filterValue === 'all') return;
        
        if (filterValue === 'blank') {
          filtered = filtered.filter(b => !b[field] || b[field] === '' || b[field] === 0);
        } else if (filterValue === 'filled') {
          filtered = filtered.filter(b => b[field] && b[field] !== '' && b[field] !== 0);
        }
      });
    }

    // Apply Other Fields filters when on other_status tab
    if (activeFieldTab === 'other_status') {
      Object.entries(otherFieldsFilters).forEach(([field, filterValue]) => {
        if (filterValue === 'all') return;
        
        if (filterValue === 'blank') {
          filtered = filtered.filter(b => !b[field] || b[field] === '');
        } else {
          filtered = filtered.filter(b => b[field]?.toLowerCase() === filterValue.toLowerCase());
        }
      });
    }

    // Apply PME filters when on pme tab
    if (activeFieldTab === 'pme') {
      Object.entries(pmeFilters).forEach(([field, filterValue]) => {
        if (filterValue === 'all') return;
        
        if (filterValue === 'blank') {
          filtered = filtered.filter(b => !b[field] || b[field] === '');
        } else if (filterValue === 'filled') {
          filtered = filtered.filter(b => b[field] && b[field] !== '');
        } else {
          filtered = filtered.filter(b => b[field]?.toLowerCase() === filterValue.toLowerCase());
        }
      });
    }

    setFilteredBookings(filtered);
  }, [bookings, searchValue, dateRange, activeFieldTab, discountFilters, otherFieldsFilters, pmeFilters, dateFieldForTab]);

  // Initial fetch
  React.useEffect(() => {
    fetchBookings();
  }, []);

  // Filter when dependencies change
  React.useEffect(() => {
    filterBookings();
  }, [filterBookings]);

  // Selection handlers
  const handleSelectBooking = (id) => {
    setSelectedBookings(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedBookings.length === filteredBookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(filteredBookings.map(b => b.id));
    }
  };

  // Inline edit handlers
  const startInlineEdit = (bookingId, field, currentValue) => {
    setEditingCell({ bookingId, field });
    setEditValue(currentValue || '');
  };

  const saveInlineEdit = async () => {
    if (!editingCell) return;
    
    try {
      await axios.post(`${BACKEND_URL}/api/bookings/bulk-edit`, {
        booking_ids: [editingCell.bookingId],
        field: editingCell.field,
        value: editValue
      }, getAuthHeader());
      
      setBookings(prev => prev.map(b => 
        b.id === editingCell.bookingId ? { ...b, [editingCell.field]: editValue } : b
      ));
      toast.success('Updated successfully');
    } catch (error) {
      console.error('Update error:', error.response?.data || error.message);
      toast.error('Failed to update: ' + (error.response?.data?.detail || error.message));
    }
    setEditingCell(null);
    setEditValue('');
  };

  const cancelInlineEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Bulk edit handler
  const handleBulkEdit = async () => {
    if (selectedBookings.length === 0) {
      toast.error('Please select at least one booking');
      return;
    }

    const updates = Object.entries(bulkEditData).filter(([_, value]) => value !== '');
    if (updates.length === 0) {
      toast.error('Please fill at least one field');
      return;
    }

    try {
      toast.info(`Updating ${selectedBookings.length} bookings...`);
      
      for (const [field, value] of updates) {
        await axios.post(`${BACKEND_URL}/api/bookings/bulk-edit`, {
          booking_ids: selectedBookings,
          field: field,
          value: value
        }, getAuthHeader());
      }

      toast.success(`Updated ${selectedBookings.length} bookings successfully!`);
      setShowBulkEditModal(false);
      setSelectedBookings([]);
      setBulkEditData({});
      fetchBookings();
    } catch (error) {
      toast.error('Failed to update bookings');
    }
  };

  // Tab change handler
  const handleTabChange = (tabId) => {
    setActiveFieldTab(tabId);
    // Reset filters when switching tabs
    if (tabId !== 'discounts') {
      setDiscountFilters({ pool_amount: 'all', scheme_discount_1: 'all', scheme_discount_2: 'all', special_discount: 'all' });
    }
    if (tabId !== 'other_status') {
      setOtherFieldsFilters({ inwards_status: 'all', address_proof_received: 'all', photos: 'all', loan_status: 'all' });
    }
    if (tabId !== 'pme') {
      setPmeFilters({ pme_submission_date: 'all', pme_status: 'all' });
    }
  };

  const value = {
    // Data
    bookings,
    filteredBookings,
    loading,
    
    // Filters
    searchValue,
    setSearchValue,
    dateRange,
    setDateRange,
    discountFilters,
    setDiscountFilters,
    otherFieldsFilters,
    setOtherFieldsFilters,
    pmeFilters,
    setPmeFilters,
    
    // Selection
    selectedBookings,
    setSelectedBookings,
    handleSelectBooking,
    handleSelectAll,
    
    // Tabs
    activeFieldTab,
    setActiveFieldTab: handleTabChange,
    activeTab,
    activeFields,
    dateFieldForTab,
    
    // Bulk edit
    showBulkEditModal,
    setShowBulkEditModal,
    bulkEditData,
    setBulkEditData,
    handleBulkEdit,
    
    // Inline edit
    editingCell,
    editValue,
    setEditValue,
    startInlineEdit,
    saveInlineEdit,
    cancelInlineEdit,
    
    // Refresh
    fetchBookings,
  };

  return (
    <MultiReportsContext.Provider value={value}>
      {children}
    </MultiReportsContext.Provider>
  );
}

export function useMultiReports() {
  const context = React.useContext(MultiReportsContext);
  if (!context) {
    throw new Error('useMultiReports must be used within a MultiReportsProvider');
  }
  return context;
}
